import _ from 'underscore';
import {
  CompareSummary,
  FishGroup,
  FishGroupSummary,
  ScenarioAwardBadges,
  Summary,
} from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-result.type';
import { aggregateFishGroupSummariesToSiteSummaries } from 'views/bluviu-nutrition/features/scenarios-v2/utils/build-summary-from-computations';

const fallbackFishGroupName = 'Unnamed fish group';
const allFishGroupsLabel = 'All (Avg. for site)';

export const ALL_FISH_GROUP_ID = 'all';
export const SELECT_ALL_KPI_ID = 'all_kpi';

export type ScenarioKpiDefinition = {
  id: string;
  nameId: string;
  tooltipId?: string;
};

type ScenarioAwardWinningValues = {
  lowestCo2?: number;
  fastestGrowth?: number;
  lowestFeedPrice?: number;
  bestBFcr?: number;
  lowestCostOfGrowth?: number;
};

export type VisibleScenarioAwardKey = 'lowestCO2' | 'lowestFeedPrice' | 'fastestGrowth' | 'bestBFcr' | 'lowestCostOfGrowth';

type RankedSummary = {
  summary: Summary;
  value: number;
};

export const getRankedSummaries = (
  summaries: Summary[],
  getValue: (summary: Summary) => number,
  sortDirection: 'asc' | 'desc'
): RankedSummary[] => {
  return summaries
    .map((summary) => ({ summary, value: getValue(summary) }))
    .filter(({ value }) => Number.isFinite(value))
    .sort((a, b) => (sortDirection === 'asc' ? a.value - b.value : b.value - a.value));
};

export const getPercentageDifference = (bestValue: number, secondValue: number): number => {
  if (secondValue === 0) return 0;

  return Math.abs(((secondValue - bestValue) / secondValue) * 100);
};

export const getVisibleAwardKeys = (badges: ScenarioAwardBadges): VisibleScenarioAwardKey[] => {
  return [
    { key: 'lowestCO2' as const, show: badges.isLowestCo2 },
    { key: 'lowestFeedPrice' as const, show: badges.isLowestFeedPrice },
    { key: 'fastestGrowth' as const, show: badges.isFastestGrowth },
    { key: 'bestBFcr' as const, show: badges.isBestBFcr },
    { key: 'lowestCostOfGrowth' as const, show: badges.isLowestCostOfGrowth },
  ]
    .filter((award) => award.show)
    .map((award) => award.key);
};

export const kpis: ScenarioKpiDefinition[] = [
  { id: SELECT_ALL_KPI_ID, nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.selectAll' },
  {
    id: 'finalWeightGrams',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.finalWeightGrams',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.finalWeightGrams',
  },
  {
    id: 'weightGainGrams',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.weightGainGrams',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.weightGainGrams',
  },
  {
    id: 'hogGainGrams',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.hogGainGrams',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.hogGainGrams',
  },
  {
    id: 'daysToReach',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.daysToReach',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.daysToReach',
  },
  {
    id: 'feedIntakeGrams',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.feedIntakeGrams',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.feedIntakeGrams',
  },
  {
    id: 'bFcr',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.bFcr',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.bFcr',
  },
  {
    id: 'eFcr',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.eFcr',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.eFcr',
  },
  {
    id: 'carcassYieldPercent',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.carcassYieldPercent',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.carcassYieldPercent',
  },
  {
    id: 'filletFatPercent',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.filletFatPercent',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.filletFatPercent',
  },
  {
    id: 'averageFeedCostPerKgFeed',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.averageFeedCostPerKgFeed',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.averageFeedCostPerKgFeed',
  },
  {
    id: 'feedCostOfGrowth',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.feedCostOfGrowth',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.feedCostOfGrowth',
  },
  {
    id: 'feedCostOfHogGain',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.feedCostOfHogGain',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.feedCostOfHogGain',
  },
  {
    id: 'feedCostTotal',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.feedCostTotal',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.feedCostTotal',
  },
  {
    id: 'operationsCostTotal',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.operationsCostTotal',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.operationsCostTotal',
  },
  {
    id: 'smoltCostTotal',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.smoltCostTotal',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.smoltCostTotal',
  },
  {
    id: 'harvestCostTotal',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.harvestCostTotal',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.harvestCostTotal',
  },
  {
    id: 'treatmentCostTotal',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.treatmentCostTotal',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.treatmentCostTotal',
  },
  {
    id: 'totalCost',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.totalCost',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.totalCost',
  },
  {
    id: 'potentialSalesValue',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.potentialSalesValue',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.potentialSalesValue',
  },
  {
    id: 'potentialProfit',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.potentialProfit',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.potentialProfit',
  },
  {
    id: 'globalWarmingPotentialCo2KgPerKgFeed',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.globalWarmingPotentialCo2KgPerKgFeed',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.globalWarmingPotentialCo2KgPerKgFeed',
  },
  {
    id: 'globalWarmingPotentialCo2KgPerKgGrowth',
    nameId: 'bluViuNutrition.viewScenarioResultGt2.kpis.labels.globalWarmingPotentialCo2KgPerKgGrowth',
    tooltipId: 'bluViuNutrition.viewScenarioResultGt2.kpis.tooltips.globalWarmingPotentialCo2KgPerKgGrowth',
  },
];

const kpiMessageIdsById = kpis.reduce<Record<string, { nameId: string; tooltipId?: string }>>((acc, kpi) => {
  acc[kpi.id] = { nameId: kpi.nameId, tooltipId: kpi.tooltipId };
  return acc;
}, {});

export const getKpiNameMessageId = (kpiId: string): string => {
  return kpiMessageIdsById[kpiId]?.nameId || 'bluViuNutrition.viewScenarioResultGt2.kpis.selectAll';
};

export const getKpiTooltipMessageId = (kpiId: string): string | undefined => {
  return kpiMessageIdsById[kpiId]?.tooltipId;
};

export const DEFAULT_VISIBLE_KPIS = kpis.filter((kpi) => kpi.id !== SELECT_ALL_KPI_ID).map((kpi) => kpi.id);

export const buildFishGroupsFromSummaries = (fishGroupSummaries: FishGroupSummary[]): FishGroup[] => {
  const groups = fishGroupSummaries
    .map((summary) => ({ id: String(summary.fishGroupId), name: summary.fishgroupName || fallbackFishGroupName }))
    .filter((group, index, arr) => arr.findIndex((item) => item.id === group.id) === index);

  return [{ id: ALL_FISH_GROUP_ID, name: allFishGroupsLabel }, ...groups];
};

export const buildAvailableFishGroupIds = (fishGroups: FishGroup[]): string[] =>
  fishGroups.filter((group) => group.id !== ALL_FISH_GROUP_ID).map((group) => group.id);

export const buildScenarioResultFromSelections = (
  siteSummaries: Summary[],
  fishGroupSummaries: FishGroupSummary[],
  appliedResultsFor: string[]
): Summary[] => {
  if (appliedResultsFor.length === 0 || appliedResultsFor.includes(ALL_FISH_GROUP_ID)) {
    return siteSummaries;
  }

  const selectedFishGroupSummaries = fishGroupSummaries.filter((summary) => appliedResultsFor.includes(String(summary.fishGroupId)));

  return aggregateFishGroupSummariesToSiteSummaries(selectedFishGroupSummaries).map((summary) => {
    const siteSummary = siteSummaries.find(
      (site) => site.feedPlanId === summary.feedPlanId || site.feedPlan === summary.feedPlan || site.id === summary.id
    );

    return {
      ...summary,
      feeds: siteSummary?.feeds ?? summary.feeds,
    };
  });
};

const getLowestValue = (scenarioResult: Summary[], getValue: (summary: Summary) => number): number | undefined => {
  const values = scenarioResult.map(getValue).filter(Number.isFinite);

  return values.length > 0 ? Math.min(...values) : undefined;
};

const getHighestValue = (scenarioResult: Summary[], getValue: (summary: Summary) => number): number | undefined => {
  const values = scenarioResult.map(getValue).filter(Number.isFinite);

  return values.length > 0 ? Math.max(...values) : undefined;
};

export const getScenarioGrowthRate = (summary: Summary): number => {
  return summary.daysToReach > 0 ? summary.weightGainGrams / summary.daysToReach : 0;
};

const buildScenarioAwardWinningValues = (scenarioResult: Summary[]): ScenarioAwardWinningValues => ({
  lowestCo2: getLowestValue(scenarioResult, (summary) => summary.globalWarmingPotentialCo2KgPerKgGrowth),
  fastestGrowth: getHighestValue(scenarioResult, getScenarioGrowthRate),
  lowestFeedPrice: getLowestValue(scenarioResult, (summary) => summary.averageFeedCostPerKgFeed),
  bestBFcr: getLowestValue(scenarioResult, (summary) => summary.bFcr),
  lowestCostOfGrowth: getLowestValue(scenarioResult, (summary) => summary.feedCostOfHogGain),
});

const isWinningValue = (value: number, winningValue?: number): boolean => {
  return winningValue !== undefined && Number.isFinite(value) && value === winningValue;
};

export const buildScenarioAwardBadges = (summary: Summary, scenarioResult: Summary[]): ScenarioAwardBadges => {
  const winningValues = buildScenarioAwardWinningValues(scenarioResult);

  return {
    isLowestCo2: isWinningValue(summary.globalWarmingPotentialCo2KgPerKgGrowth, winningValues.lowestCo2),
    isFastestGrowth: isWinningValue(getScenarioGrowthRate(summary), winningValues.fastestGrowth),
    isLowestFeedPrice: isWinningValue(summary.averageFeedCostPerKgFeed, winningValues.lowestFeedPrice),
    isBestBFcr: isWinningValue(summary.bFcr, winningValues.bestBFcr),
    isLowestCostOfGrowth: isWinningValue(summary.feedCostOfHogGain, winningValues.lowestCostOfGrowth),
  };
};

export const buildCompareFeedPrograms = (scenarioResult: Summary[]): CompareSummary[] => {
  if (!Array.isArray(scenarioResult)) return [];

  const sortedResult = _.sortBy(scenarioResult, 'feedPlan').reverse();

  return sortedResult.map((summary, index) => {
    return {
      ...summary,
      feedCostTotal: summary.costBreakdown?.feedCostTotal,
      operationsCostTotal: summary.costBreakdown?.operationsCostTotal,
      treatmentCostTotal: summary.costBreakdown?.treatmentCostTotal,
      smoltCostTotal: summary.costBreakdown?.smoltCostTotal,
      harvestCostTotal: summary.costBreakdown?.harvestCostTotal,
      feedProgramId: summary.id,
      id: summary.id || summary.feedPlan || `program-${index}`,
      name: summary.feedPlan || `Feed Program ${index + 1}`,
      badges: buildScenarioAwardBadges(summary, sortedResult),
    };
  });
};
